// ============================================================
// HOOKED ENGINE — PLAYER ENTITY        [Tier 2 harvest: shape]
// Universal angler rendering: on foot, in boat, mid-cast.
//
// v3 — PERSISTENT IDENTITY. The angler now wears the Outfitter:
// coat, cap, face, back item, aura, and fishing-line color all
// come from an `outfit` render-prop object (getOutfit(angler)).
// The aura renders OUTSIDE the sprite/vector, so it survives
// the day real .webp art lands. Resolver law untouched: real
// sprites win the body; the aura rides along either way.
//
// Multi-instance fix: SVG gradient ids now derive from useId()
// (Wylly's live preview mounts a second AnglerVector — fixed
// ids would cross-bleed document-wide, the HoloFoil trap).
//
// Props:
//   pos        { x, y }   — tile position
//   facing     { x, y }   — direction (-1/0/1)
//   gender     "f" | "m"
//   mode       "walk" | "boat" | "cast" | "reel"
//   walkFrame  boolean    — alternates for walk/crank animation
//   isIdle     boolean
//   idleFrame  boolean
//   tile       number     — tile size in px
//   outfit     render props from getOutfit(angler) (optional)
// ============================================================

import { useState, useEffect, useRef, useId } from "react";
import { SD, GM } from "./theme";
import {
  getPlayerSprite, getIdleSprite, getActionSprite, getBoatSprite,
} from "./SpriteManifest";
// THE MARK — caP'n's cross-game player-identity card. Hooked has no save
// data or account system of its own (a single local angler, no uid, no
// profile), so the binding here is the Empire-wide one: useIEAuth() reads
// a real signed-in uid or a cross-game handoff guest pass, never a fake
// invented player. No uid in hand (the common case, arriving cold with no
// handoff) simply means no chip renders — nothing fakes an identity that
// isn't there.
import { useIEAuth } from "./ieIdentity";
import Mark, { MarkChip } from "./Mark";

// Outfit defaults — the day-one kit's look, used when no outfit
// prop arrives (keeps every old call site rendering identically).
const DEFAULT_OUTFIT = {
  coat: [GM.goldBright, GM.goldDeep],
  cap: GM.rope, capBand: GM.ropeDim,
  face: null, back: null, backColor: GM.rope,
  aura: null, lineColor: SD.foam,
};

export function PlayerEntity({
  pos,
  facing    = { x: 0, y: 1 },
  gender    = "f",
  mode      = "walk",
  walkFrame = false,
  isIdle    = false,
  idleFrame = false,
  tile      = 48,
  outfit    = null,
}) {
  const o = { ...DEFAULT_OUTFIT, ...(outfit || {}) };
  const boat = mode === "boat";
  const size = Math.floor(tile * (boat ? 2.1 : 1.5));

  let src;
  if (boat)                    src = getBoatSprite(facing.x, facing.y);
  else if (mode === "cast")    src = getActionSprite(gender, "cast", walkFrame);
  else if (mode === "reel")    src = getActionSprite(gender, "reel", walkFrame);
  else if (isIdle)             src = getIdleSprite(gender, idleFrame);
  else                         src = getPlayerSprite(gender, facing.x, facing.y, walkFrame);

  const [imgErr, setImgErr] = useState(false);
  const prevSrc = useRef(src);
  useEffect(() => {
    if (prevSrc.current !== src) { prevSrc.current = src; setImgErr(false); }
  }, [src]);

  // THE MARK — see import note above. viewingSelf's own card, opened by
  // clicking the little nameplate over their own angler.
  const { uid: markUid } = useIEAuth();
  const [markOpen, setMarkOpen] = useState(false);

  // Breathing: only on foot & idle, so it never fights the walk,
  // cast, or boat-bob animations. A whisper of scaleY, nothing more.
  const breathing = isIdle && !boat && mode !== "cast" && mode !== "reel";

  return (
    <>
    <div style={{
      position: "absolute",
      left: pos.x * tile - size / 2 + tile / 2,
      top:  pos.y * tile - size + tile,
      width: size, height: size,
      zIndex: 10,
      transition: "left 0.08s, top 0.08s",
      pointerEvents: "none",
    }}>
      {/* THE MARK — the one bit of this box allowed to take clicks. */}
      {markUid && (
        <div style={{
          position: "absolute", left: "50%", top: -22,
          transform: "translateX(-50%)", pointerEvents: "auto", zIndex: 11,
        }}>
          <MarkChip uid={markUid} dense onOpen={() => setMarkOpen(true)} />
        </div>
      )}
      {/* the aura — outside the body, sprite-proof */}
      {o.aura && (
        <div style={{
          position: "absolute", left: "50%", top: "60%",
          width: size * 1.05, height: size * 0.62,
          transform: "translate(-50%,-50%)",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${o.aura}55 0%, ${o.aura}22 45%, transparent 72%)`,
          filter: "blur(3px)",
          animation: "moonShimmer 3.5s ease-in-out infinite",
          pointerEvents: "none",
        }} />
      )}

      {/* LANTERN RIM-LIGHT — the wharf lights the angler. A soft
          warm glow hugging the figure so he belongs to the scene
          instead of floating on it. Sprite- and outfit-proof: it
          rides outside the body like the aura. Kept subtle. */}
      <div style={{
        position: "absolute", left: "50%", top: "46%",
        width: size * 0.86, height: size * 0.86,
        transform: "translate(-50%,-50%)", borderRadius: "50%",
        background: `radial-gradient(circle at 50% 40%, ${GM.gold}1c 0%, ${GM.gold}0a 46%, transparent 70%)`,
        mixBlendMode: "screen", filter: "blur(2px)",
        pointerEvents: "none",
      }} />

      {/* the figure — breathes when idle on foot */}
      <div style={{
        width: "100%", height: "100%", position: "relative",
        transformOrigin: "50% 100%",
        animation: breathing ? "anglerBreathe 4s ease-in-out infinite" : "none",
      }}>
        {src && !imgErr
          ? <img
              key={src}
              src={src}
              alt="angler"
              onError={() => setImgErr(true)}
              style={{ width: "100%", height: "100%", imageRendering: "pixelated", position: "relative" }}
            />
          : <AnglerVector boat={boat} mode={mode} facing={facing} walkFrame={walkFrame} isIdle={isIdle} size={size} outfit={o} />
        }
      </div>
    </div>
    {markOpen && (
      <Mark uid={markUid} viewingSelf onClose={() => setMarkOpen(false)} />
    )}
    </>
  );
}

// ── The vector angler — the designed glyph fallback ──────────
// Exported: Wylly's mounts it as the live fitting-room preview.
export function AnglerVector({ boat, mode, facing = { x: 0, y: 1 }, walkFrame, isIdle, size, outfit = null }) {
  const o = { ...DEFAULT_OUTFIT, ...(outfit || {}) };
  const uid = useId();
  const coatId = `coat-${uid}`;
  const hullId = `hull-${uid}`;
  const flip = facing.x < 0;
  // soft ground/water contact shadow under everyone
  const shadow = (
    <ellipse cx="50" cy="92" rx="20" ry="4" fill="rgba(0,0,0,0.5)" />
  );

  if (boat) {
    return (
      <div style={{ width: "100%", height: "100%", transform: flip ? "scaleX(-1)" : "none",
        filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.6))",
        animation: "bobberIdle 3.4s ease-in-out infinite", position: "relative" }}>
        <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ overflow: "visible" }}>
          <defs>
            <linearGradient id={hullId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={GM.ropeLight} />
              <stop offset="100%" stopColor={GM.ropeDim} />
            </linearGradient>
          </defs>
          {/* wake — trailing ripples so the boat moves THROUGH water */}
          <ellipse cx="50" cy="80" rx="34" ry="7" fill={SD.caustic} opacity="0.18" />
          <ellipse cx="50" cy="84" rx="24" ry="5" fill={SD.foam} opacity="0.12"
            style={{ animation: "boatWake 1.6s ease-out infinite" }} />
          <ellipse cx="50" cy="86" rx="16" ry="4" fill={SD.foam} opacity="0.1"
            style={{ animation: "boatWake 1.6s ease-out 0.5s infinite" }} />
          {/* hull */}
          <path d="M14,64 Q50,86 86,64 L80,72 Q50,90 20,72 Z" fill={`url(#${hullId})`} stroke={GM.pageDeep} strokeWidth="1.5" />
          {/* inner plank */}
          <path d="M20,64 Q50,78 80,64" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="1.2" />
          {/* seat */}
          <rect x="40" y="58" width="20" height="4" rx="1" fill={GM.pageDeep} />
          {/* little angler seated — coat + cap from the Outfitter */}
          <circle cx="50" cy="42" r="7" fill={SD.starSilver} stroke={GM.pageDeep} strokeWidth="1" />
          <path d="M43,39 Q50,32 57,39 Z" fill={o.cap} stroke={GM.pageDeep} strokeWidth="0.6" />
          <rect x="44" y="48" width="12" height="14" rx="3" fill={o.coat[0]} />
          {/* rod + line (line color from the Outfitter) */}
          <line x1="56" y1="50" x2="82" y2="26" stroke={GM.pageWarm} strokeWidth="1.6" strokeLinecap="round" />
          <line x1="82" y1="26" x2="84" y2="52" stroke={o.lineColor} strokeWidth="0.5" />
        </svg>
      </div>
    );
  }

  const casting = mode === "cast";
  const reeling = mode === "reel";
  const bob = isIdle ? { animation: "fishHover 3s ease-in-out infinite" } : {};

  return (
    <div style={{ width: "100%", height: "100%", transform: flip ? "scaleX(-1)" : "none",
      filter: "drop-shadow(0 3px 5px rgba(0,0,0,0.6))", position: "relative", ...bob }}>
      <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ overflow: "visible" }}>
        <defs>
          <linearGradient id={coatId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={o.coat[0]} />
            <stop offset="100%" stopColor={o.coat[1]} />
          </linearGradient>
        </defs>
        {shadow}
        {/* back item — drawn behind the body */}
        {o.back === "coil" && (
          <>
            <circle cx="36" cy="52" r="8" fill="none" stroke={o.backColor} strokeWidth="4" />
            <circle cx="36" cy="52" r="8" fill="none" stroke="rgba(0,0,0,0.3)" strokeWidth="1" />
          </>
        )}
        {o.back === "creel" && (
          <>
            <rect x="28" y="46" width="14" height="16" rx="3" fill={o.backColor} stroke={GM.pageDeep} strokeWidth="1" />
            <line x1="28" x2="42" y1="51" y2="51" stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
            <line x1="28" x2="42" y1="56" y2="56" stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
          </>
        )}
        {o.back === "net" && (
          <>
            <rect x="29" y="45" width="12" height="18" rx="4" fill="none" stroke={o.backColor} strokeWidth="1.4" />
            <line x1="29" x2="41" y1="51" y2="51" stroke={o.backColor} strokeWidth="0.8" />
            <line x1="29" x2="41" y1="57" y2="57" stroke={o.backColor} strokeWidth="0.8" />
            <line x1="35" x2="35" y1="45" y2="63" stroke={o.backColor} strokeWidth="0.8" />
          </>
        )}
        {o.back === "lantern" && (
          <>
            <line x1="38" y1="44" x2="34" y2="50" stroke={GM.ropeDim} strokeWidth="1.5" />
            <rect x="30" y="50" width="8" height="10" rx="2" fill={GM.pageDeep} stroke={o.backColor} strokeWidth="1" />
            <rect x="32" y="52" width="4" height="6" rx="1" fill={o.backColor}
              style={{ animation: "lanternFlicker 3s ease-in-out infinite" }} />
          </>
        )}
        {/* legs — alternate on walkFrame for a stride */}
        <rect x={walkFrame ? 42 : 44} y="70" width="6" height="18" rx="2" fill={GM.pageWarm} />
        <rect x={walkFrame ? 52 : 50} y="70" width="6" height="18" rx="2" fill={GM.panel} />
        {/* body / coat — the Outfitter's gradient */}
        <path d="M40,44 Q50,40 60,44 L58,72 Q50,76 42,72 Z" fill={`url(#${coatId})`} stroke={GM.pageDeep} strokeWidth="1" />
        {/* head + cap from the Outfitter */}
        <circle cx="50" cy="34" r="9" fill={SD.starSilver} stroke={GM.pageDeep} strokeWidth="1" />
        <path d="M40,30 Q50,20 60,30 Z" fill={o.cap} stroke={GM.pageDeep} strokeWidth="0.8" />
        <rect x="38" y="29" width="24" height="3" rx="1.5" fill={o.capBand} />
        {/* face item */}
        {o.face === "spectacles" && (
          <>
            <circle cx="46" cy="35" r="2.6" fill="none" stroke={GM.silverBright} strokeWidth="0.9" />
            <circle cx="54" cy="35" r="2.6" fill="none" stroke={GM.silverBright} strokeWidth="0.9" />
            <line x1="48.6" x2="51.4" y1="35" y2="35" stroke={GM.silverBright} strokeWidth="0.9" />
          </>
        )}
        {o.face === "eyepatch" && (
          <>
            <circle cx="54" cy="34.5" r="2.6" fill={GM.pageDeep} />
            <line x1="42" x2="58" y1="31.5" y2="33" stroke={GM.pageDeep} strokeWidth="1.1" />
          </>
        )}
        {o.face === "mask" && (
          <rect x="43" y="32" width="14" height="5" rx="2.5" fill={GM.silver} stroke={GM.silverDim} strokeWidth="0.6" opacity="0.92" />
        )}
        {/* arm + rod, posed by mode — line color from the Outfitter */}
        {casting ? (
          <>
            <line x1="56" y1="52" x2="30" y2="30" stroke={SD.starSilver} strokeWidth="4" strokeLinecap="round" />
            <line x1="30" y1="30" x2="70" y2="8" stroke={GM.pageWarm} strokeWidth="2" strokeLinecap="round" />
            <line x1="70" y1="8" x2="72" y2="40" stroke={o.lineColor} strokeWidth="0.5" />
          </>
        ) : reeling ? (
          <>
            <line x1="56" y1="52" x2="72" y2={walkFrame ? 44 : 56} stroke={SD.starSilver} strokeWidth="4" strokeLinecap="round" />
            <line x1="72" y1={walkFrame ? 44 : 56} x2="88" y2="30" stroke={GM.pageWarm} strokeWidth="2" strokeLinecap="round" />
            <circle cx={72} cy={walkFrame ? 44 : 56} r="3" fill={GM.gold} stroke={GM.pageDeep} strokeWidth="0.6" />
          </>
        ) : (
          <>
            <line x1="56" y1="52" x2="66" y2="60" stroke={SD.starSilver} strokeWidth="4" strokeLinecap="round" />
            <line x1="66" y1="60" x2="86" y2="20" stroke={GM.pageWarm} strokeWidth="1.8" strokeLinecap="round" />
          </>
        )}
      </svg>
    </div>
  );
}

// ── Walk/idle animation hook — verbatim harvest ──────────────
export function usePlayerAnimation(pos) {
  const [walkFrame, setWalkFrame] = useState(false);
  const [isIdle, setIsIdle]       = useState(false);
  const [idleFrame, setIdleFrame] = useState(false);
  const walkFrameRef = useRef(false);
  const lastPosRef   = useRef({ x: pos.x, y: pos.y });
  const idleTimerRef = useRef(null);
  const idleAnimRef  = useRef(null);

  useEffect(() => {
    if (pos.x !== lastPosRef.current.x || pos.y !== lastPosRef.current.y) {
      lastPosRef.current = { x: pos.x, y: pos.y };
      walkFrameRef.current = !walkFrameRef.current;
      setWalkFrame(walkFrameRef.current);
      setIsIdle(false);
      setIdleFrame(false);
      clearTimeout(idleTimerRef.current);
      clearInterval(idleAnimRef.current);
      idleTimerRef.current = setTimeout(() => {
        setIsIdle(true);
        idleAnimRef.current = setInterval(() => setIdleFrame(f => !f), 1800);
      }, 3000);
    }
    return () => {
      clearTimeout(idleTimerRef.current);
      clearInterval(idleAnimRef.current);
    };
  }, [pos.x, pos.y]);

  return { walkFrame, isIdle, idleFrame };
}

// ── Mobile D-Pad — verbatim harvest, wharf retheme (brass) ───
export function MobileDpad({ keysRef }) {
  const dirs = [
    { key: "ArrowUp",    label: "▲", col: 2, row: 1 },
    { key: "ArrowLeft",  label: "◀", col: 1, row: 2 },
    { key: "ArrowDown",  label: "▼", col: 2, row: 3 },
    { key: "ArrowRight", label: "▶", col: 3, row: 2 },
  ];
  const press   = (key) => { keysRef.current[key] = true; };
  const release = (key) => { keysRef.current[key] = false; };
  const releaseAll = () => {
    ["ArrowUp","ArrowDown","ArrowLeft","ArrowRight"].forEach(k => { keysRef.current[k] = false; });
  };
  const btn = (col, row) => ({
    gridColumn: col, gridRow: row,
    width: 50, height: 50,
    background: `linear-gradient(180deg, ${GM.panelHi}, ${GM.pageDeep})`,
    border: `1px solid ${GM.gold}55`,
    borderRadius: 12, color: GM.goldBright, fontSize: 18,
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", userSelect: "none", WebkitUserSelect: "none",
    touchAction: "none",
    boxShadow: `0 2px 10px rgba(0,0,0,0.7), inset 0 1px 0 ${GM.goldPale}22, inset 0 -2px 0 rgba(0,0,0,0.4)`,
  });
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 20, zIndex: 200,
      display: "grid",
      gridTemplateColumns: "50px 50px 50px",
      gridTemplateRows: "50px 50px 50px",
      gap: 6, touchAction: "none",
    }}>
      {dirs.map(({ key, label, col, row }) => (
        <div key={key} style={btn(col, row)}
          onPointerDown={e => { e.preventDefault(); press(key); }}
          onPointerUp={e => { e.preventDefault(); release(key); }}
          onPointerLeave={e => { e.preventDefault(); release(key); }}
          onPointerCancel={e => { e.preventDefault(); releaseAll(); }}>
          {label}
        </div>
      ))}
    </div>
  );
}
